export type SwipeRequirementType = "velocity" | "position";

export interface SwipeStackTuning {
  flickOnSwipe: boolean;
  swipeRequirementType: SwipeRequirementType;
  /** Velocity threshold (velocity mode) or px offset (position mode) */
  swipeThreshold: number;
  /** Position mode: derive threshold from viewport width × percent / 100 */
  useScreenWidthThreshold: boolean;
  screenWidthThresholdPercent: number;
  /** Cards rendered ahead in the stack (current + N) */
  stackLookahead: number;
}

export const SWIPE_STACK_TUNING_STORAGE_KEY = "fitties-swipe-stack-tuning-v2";

const LEGACY_SWIPE_STACK_TUNING_STORAGE_KEY = "fitties-swipe-stack-tuning";

export const DEFAULT_SWIPE_STACK_TUNING: SwipeStackTuning = {
  flickOnSwipe: true,
  swipeRequirementType: "position",
  swipeThreshold: 0.5,
  useScreenWidthThreshold: true,
  screenWidthThresholdPercent: 28,
  stackLookahead: 2,
};

/** Production swipe defaults — distance-based commit (~28% screen width). */
export const TINDER_SWIPE_STACK_PRESET: SwipeStackTuning = DEFAULT_SWIPE_STACK_TUNING;

export function isSwipeStackTuningEnabled(): boolean {
  return import.meta.env.DEV || import.meta.env.VITE_ENABLE_SWIPE_TUNING === "true";
}

export function resolveSwipeThreshold(tuning: SwipeStackTuning, viewportWidth: number): number {
  if (tuning.swipeRequirementType === "position" && tuning.useScreenWidthThreshold) {
    return Math.round((viewportWidth * tuning.screenWidthThresholdPercent) / 100);
  }
  return tuning.swipeThreshold;
}

export function suggestedPositionThresholdPx(
  viewportWidth: number,
  percent = DEFAULT_SWIPE_STACK_TUNING.screenWidthThresholdPercent
): number {
  return Math.round((viewportWidth * percent) / 100);
}

type SwipeStackTuningRaw = {
  swipeRequirementType?: unknown;
  flickOnSwipe?: unknown;
  swipeThreshold?: unknown;
  useScreenWidthThreshold?: unknown;
  screenWidthThresholdPercent?: unknown;
  stackLookahead?: unknown;
};

export function parseSwipeStackTuning(raw: unknown): SwipeStackTuning | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const o = raw as SwipeStackTuningRaw;
  const type = o.swipeRequirementType;
  if (type !== "velocity" && type !== "position") {
    return null;
  }
  return {
    flickOnSwipe: Boolean(o.flickOnSwipe),
    swipeRequirementType: type,
    swipeThreshold: Number(o.swipeThreshold) || DEFAULT_SWIPE_STACK_TUNING.swipeThreshold,
    useScreenWidthThreshold: Boolean(o.useScreenWidthThreshold),
    screenWidthThresholdPercent:
      Number(o.screenWidthThresholdPercent) || DEFAULT_SWIPE_STACK_TUNING.screenWidthThresholdPercent,
    stackLookahead: (() => {
      const n = Number(o.stackLookahead);
      if (Number.isFinite(n)) {
        return Math.min(3, Math.max(0, Math.round(n)));
      }
      return DEFAULT_SWIPE_STACK_TUNING.stackLookahead;
    })(),
  };
}

export function loadSwipeStackTuning(): SwipeStackTuning {
  if (typeof window === "undefined") {
    return DEFAULT_SWIPE_STACK_TUNING;
  }
  try {
    const stored = window.localStorage.getItem(SWIPE_STACK_TUNING_STORAGE_KEY);
    if (!stored) {
      return DEFAULT_SWIPE_STACK_TUNING;
    }
    const parsed = parseSwipeStackTuning(JSON.parse(stored));
    return parsed ?? DEFAULT_SWIPE_STACK_TUNING;
  } catch {
    return DEFAULT_SWIPE_STACK_TUNING;
  }
}

export function saveSwipeStackTuning(tuning: SwipeStackTuning): void {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(LEGACY_SWIPE_STACK_TUNING_STORAGE_KEY);
  window.localStorage.setItem(SWIPE_STACK_TUNING_STORAGE_KEY, JSON.stringify(tuning));
}
